import { i18n } from '@lingui/core';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { AlertTriangle, PlayCircle, Plug, RefreshCw, Wand2, type LucideIcon } from 'lucide-react';
import { AgenticProcess, Project, TypeId, dataContext, type TerminalRuntimeError } from '@sdk';
import { notify } from '@src/notifications';
import { Button } from '@src/components/ui/button';
import { CopyButton } from '@src/components/ui/copy-button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@src/components/ui/tooltip';
import { useContext as useDataContext } from '@src/hooks/useContext';

/**
 * Per-kind copy + primary action for the runtime-error banner. Each entry
 * describes what the user sees and what the action does when clicked.
 *
 * Severity-wise these are all "soft" failures from
 * ``ProcessLoadError``: the entity exists, only the runtime needs help.
 * The route loader has already populated
 * ``dataContext.terminalRuntimeError`` and kept the user on the URL they
 * requested; this banner is what makes the recovery surface visible.
 */
interface KindConfig {
  icon: LucideIcon;
  /**
   * Copy is held as lazy {@link MessageDescriptor}s, resolved with `i18n._` at
   * render. This table is module-level, so a `t` macro would resolve once at
   * import — before a catalog is active — and freeze the boot locale's English.
   */
  /** Compact one-line headline shown on the banner. */
  title: MessageDescriptor;
  /** Subtext under the title (optional). */
  detail?: MessageDescriptor;
  /** Action button label + handler factory. */
  actionLabel: MessageDescriptor;
  actionIcon: LucideIcon;
  /** Async — returns true on success so the banner can self-dismiss. */
  action: (processId: string) => Promise<boolean>;
}

async function loadProcessById(processId: string): Promise<AgenticProcess | null> {
  const cached = AgenticProcess.getByIdFromCache<AgenticProcess>(processId);
  if (cached) return cached;
  try {
    return (await AgenticProcess.getById<AgenticProcess>(processId)) ?? null;
  } catch {
    return null;
  }
}

/**
 * Retry `)`process.start({visible:true})``. Used by both
 * ``runtime_terminated`` (the process stopped) and ``pty_attach_failed``
 * (PTY died) — the same backend `open` action is idempotent and handles
 * both cases, so the recovery code path is shared.
 */
async function retryStart(processId: string): Promise<boolean> {
  const process = await loadProcessById(processId);
  if (!process) {
    notify.error({ title: i18n._(msg`Couldn’t resolve this process.`), id: `terminal-recover:${processId}` });
    return false;
  }
  try {
    await process.start({ visible: true });
    notify.success({ title: i18n._(msg`Reconnected.`), id: `terminal-recover:${processId}` });
    dataContext.setTerminalRuntimeError(null);
    return true;
  } catch (err) {
    notify.error({
      title: err instanceof Error ? err.message : 'Reconnect failed.',
      id: `terminal-recover:${processId}`,
    });
    return false;
  }
}

/**
 * Explicit user retry of a failed-to-start process. ``retry: true`` is what
 * clears the server-side ``start_failure`` latch — a plain ``start()`` would
 * be refused (that refusal is the loop-breaker; only a user click re-arms).
 * Exported: the shell-less panel dead-end (`TerminalPanelErrorState`) shares
 * this exact recovery, so the retry semantics live in one place.
 */
export async function retryFailedStart(processId: string): Promise<boolean> {
  const process = await loadProcessById(processId);
  if (!process) {
    notify.error({ title: i18n._(msg`Couldn’t resolve this process.`), id: `terminal-recover:${processId}` });
    return false;
  }
  try {
    await process.start({ visible: true, retry: true });
    notify.success({ title: i18n._(msg`Relaunched.`), id: `terminal-recover:${processId}` });
    dataContext.setTerminalRuntimeError(null);
    return true;
  } catch (err) {
    notify.error({ title: err instanceof Error ? err.message : 'Retry failed.', id: `terminal-recover:${processId}` });
    return false;
  }
}

async function recoverProject(processId: string): Promise<boolean> {
  const process = await loadProcessById(processId);
  if (!process) {
    notify.error({ title: i18n._(msg`Couldn’t resolve this process.`), id: `terminal-recover:${processId}` });
    return false;
  }
  try {
    const recovered = await process.recoverProject();
    if (!recovered) {
      notify.error({
        title: i18n._(msg`Project not recoverable from this workdir.`),
        id: `terminal-recover:${processId}`,
      });
      return false;
    }
    await dataContext.setContextEntityTypeId(
      // ContextEntitiesEnum.CurrentProjectTypeId mirrored as a literal so
      // we don't drag the whole enum import in here.
      'CurrentProjectTypeId' as never,
      new TypeId(Project.type, recovered.id),
    );
    notify.success({ title: i18n._(msg`Project recovered.`), id: `terminal-recover:${processId}` });
    dataContext.setTerminalRuntimeError(null);
    return true;
  } catch (err) {
    notify.error({
      title: err instanceof Error ? err.message : 'Project recovery failed.',
      id: `terminal-recover:${processId}`,
    });
    return false;
  }
}

// Not `async`: there is nothing to await, and the reload ends this page anyway.
// The Promise return keeps it assignable to `KindConfig.action` alongside the
// handlers that genuinely are async.
function retryNetwork(): Promise<boolean> {
  // The simplest reliable retry is a full reload. Anything finer-grained
  // would need to know which fetch failed; ``network_error`` says only
  // "the entity fetch threw". A reload re-runs the route loader.
  window.location.reload();
  return Promise.resolve(true);
}

const KIND_CONFIG: Record<TerminalRuntimeError['kind'], KindConfig> = {
  runtime_terminated: {
    icon: AlertTriangle,
    title: msg`This process has stopped.`,
    detail: msg`Click Restart to spawn a fresh PTY.`,
    actionLabel: msg`Restart`,
    actionIcon: PlayCircle,
    action: retryStart,
  },
  pty_attach_failed: {
    icon: Plug,
    title: msg`PTY disconnected.`,
    detail: msg`The backend may have restarted. Click to reattach.`,
    actionLabel: msg`Reconnect`,
    actionIcon: Plug,
    action: retryStart,
  },
  shell_entity_missing: {
    icon: AlertTriangle,
    title: msg`Shell record is missing for this process.`,
    detail: msg`A fresh shell needs to be allocated. (Backend action coming — for now Restart will retry.)`,
    actionLabel: msg`Restart`,
    actionIcon: PlayCircle,
    action: retryStart,
  },
  project_missing: {
    icon: Wand2,
    title: msg`This process points to a deleted project.`,
    detail: msg`Recover it from the workdir, or pick a new project via the project chip.`,
    actionLabel: msg`Recover project`,
    actionIcon: Wand2,
    action: recoverProject,
  },
  network_error: {
    icon: RefreshCw,
    title: msg`Couldn’t reach the backend.`,
    detail: msg`The fetch failed. Retry once the backend is up.`,
    actionLabel: msg`Retry`,
    actionIcon: RefreshCw,
    action: retryNetwork,
  },
  project_mismatch: {
    icon: AlertTriangle,
    title: msg`This session belongs to a different project.`,
    detail: msg`The transcript on disk was started under another project — the binding here is frozen to avoid silent drift. Reload to re-resolve, or open the session under its real project.`,
    actionLabel: msg`Reload`,
    actionIcon: RefreshCw,
    action: retryNetwork,
  },
  failed_to_start: {
    icon: AlertTriangle,
    title: msg`Failed to start.`,
    detail: msg`The worker exited immediately after launch, so auto-relaunch is paused. Retry to launch it again.`,
    actionLabel: msg`Retry`,
    actionIcon: RefreshCw,
    action: retryFailedStart,
  },
};

/**
 * Banner shown above the xterm canvas when the route loader recorded a
 * soft runtime failure. The route already kept the user on their
 * requested URL — this is the recovery affordance.
 *
 * Renders nothing when ``dataContext.terminalRuntimeError`` is null. When
 * set, picks per-kind copy + action from KIND_CONFIG and lets the user
 * fire the recovery in one click.
 */
export function TerminalRuntimeErrorBanner({ processId }: { processId?: string }) {
  const { terminalRuntimeError } = useDataContext();
  const [busy, setBusy] = useState(false);
  const { t } = useLingui();

  if (!terminalRuntimeError) return null;
  // The error names ONE process, and this component is mounted by every
  // terminal — so a failure had been showing over whichever terminal you
  // happened to be looking at. A plain shell has no process at all and was
  // reporting a Claude session's launch failure above its own prompt, with a
  // Retry button that would restart something else entirely.
  //
  // A caller with no process passes none, which is exactly the plain-terminal
  // case: nothing here can own a process failure, so nothing is shown.
  if (processId !== terminalRuntimeError.processId) return null;
  const cfg = KIND_CONFIG[terminalRuntimeError.kind];
  if (!cfg) return null;

  // failed_to_start carries a concrete server-recorded reason on the entity
  // (`start_failure`, e.g. "Worker exited 0.9s after launch (exit code 1)").
  // Prefer it over the generic copy when the process is in cache.
  const latchedReason =
    terminalRuntimeError.kind === 'failed_to_start'
      ? AgenticProcess.getByIdFromCache<AgenticProcess>(terminalRuntimeError.processId)?.start_failure
      : null;
  const detail = latchedReason
    ? t`${latchedReason} Auto-relaunch is paused — Retry to launch again.`
    : cfg.detail && i18n._(cfg.detail);

  const Icon = cfg.icon;
  const ActionIcon = cfg.actionIcon;

  return (
    // Its own provider: this banner mounts in several hosts and cannot assume
    // one above it.
    <TooltipProvider delayDuration={200}>
      <div
        className="flex items-center gap-3 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-[12px] text-amber-900 dark:text-amber-200"
        data-testid="terminal-runtime-error-banner"
        data-error-kind={terminalRuntimeError.kind}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="font-medium">{i18n._(cfg.title)}</div>
          {detail && (
            // ONE line, whatever the backend sent.
            //
            // `start_failure` is a verbatim server sentence and some of them are
            // enormous: a spawn refusal quotes the whole worker PATH, which on
            // Windows ran to a dozen wrapped lines and pushed the terminal off
            // screen behind its own error. Truncating is not hiding — the full
            // text is one hover away and one click from the clipboard, which is
            // what it is actually for: pasting into a bug report.
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="truncate text-[11px] opacity-80" data-testid="terminal-runtime-error-banner-detail">
                  {detail}
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" className="max-h-60 max-w-xl overflow-auto">
                {/* `whitespace-pre-wrap` + `break-all`: the long ones are
                  separator-packed PATH strings with no spaces to wrap at, so
                  without this the tooltip is one unreadable line. */}
                <p className="whitespace-pre-wrap break-all text-[11px]">{detail}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {/* Only a SERVER sentence is worth a copy button. `cfg.detail` is our own
            boilerplate ("Click Restart to spawn a fresh PTY") — offering to put
            that on the clipboard is an affordance for nothing, and it is the
            long verbatim ones that people actually need to paste into a report. */}
        {latchedReason && (
          <CopyButton
            // `latchedReason` gates this, so `detail` is the composed sentence —
            // but that implication is not one TS can follow, and the fallback is
            // the raw server text either way.
            value={detail ?? latchedReason}
            title={t`Copy the full error`}
            testId="terminal-runtime-error-banner-copy"
            className="shrink-0 rounded p-1 opacity-70 hover:opacity-100"
            iconClassName="h-3.5 w-3.5"
          />
        )}
        <Button
          type="button"
          size="sm"
          variant="default"
          disabled={busy}
          onClick={() => {
            // `onClick` wants a void return, so the async work is fired rather
            // than returned — an unhandled rejection here would be invisible.
            void (async () => {
              setBusy(true);
              try {
                await cfg.action(terminalRuntimeError.processId);
              } finally {
                setBusy(false);
              }
            })();
          }}
          data-testid="terminal-runtime-error-banner-action"
          className="shrink-0"
        >
          <ActionIcon className="h-3.5 w-3.5" />
          {busy ? t`Working…` : i18n._(cfg.actionLabel)}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => dataContext.setTerminalRuntimeError(null)}
          data-testid="terminal-runtime-error-banner-dismiss"
          className="shrink-0 opacity-70 hover:opacity-100"
          title={t`Dismiss banner (the runtime error stays — this just hides the affordance)`}
        >
          <Trans>Dismiss</Trans>
        </Button>
      </div>
    </TooltipProvider>
  );
}
